import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Grid,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import Plus from '@untitled-ui/icons-react/build/esm/Plus';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import {
  getAllMdsFormAction,
  getMdsFormIdAction,
  getMdsReformId,
} from 'src/redux/actions/mdsCommunicationActions';
import { paths } from 'src/paths';
import ListingTable from 'src/sections/dashboard/patients/patient-details-listing-table';
import { MultiSelect } from 'src/components/multi-select';
import useCheckRole from 'src/hooks/use-check-role';
import { getSinglePatient } from 'src/redux/actions/patientAction';

interface Option {
  label: string;
  value: string;
}

const statusOptions: Option[] = [
  {
    label: 'In-Progress',
    value: 'IN_PROGRESS',
  },
  {
    label: 'Finalized',
    value: 'FINALIZED',
  },
];

type SortOption = {
  sortBy: string;
  sortDirection: 'desc' | 'asc';
};

function MdsCommunication() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { patientId } = location.state;
  const getMdsFormList = useSelector((state: any) => state.getAllMdsFormReducer.response);
  const loadMdsFormList = useSelector((state: any) => state.getAllMdsFormReducer.loading);
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const {
    patientDetails,
    patients,
    mdsCommunication,
    editMdsCommunication,
    viewMdsCommunication,
    evalMds,
    recertiMds,
    dischargeMds,
  } = paths;
  const [rowPerPage, setRowPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [filterStatus, setStatusFilter] = useState<string[]>([]);
  const [sortOptions, setSortOptions] = useState<SortOption>({
    sortBy: 'assessmentDate',
    sortDirection: 'desc',
  });
  const [isDor] = useCheckRole('dor');

  const setInitialPage = () => {
    setRowPerPage(10);
    setPage(0);
  };

  useEffect(() => {
    dispatch(
      getSinglePatient({
        patientId: patientId,
      })
    );
  }, []);
  const callGetMdsList = (status?: string | null) => {
    dispatch(
      getAllMdsFormAction(patientId, {
        status,
        sortBy: sortOptions.sortBy,
        sortDirection: sortOptions.sortDirection || 'desc',
        page,
        pageSize: rowPerPage,
      })
    );
  };

  useEffect(() => {
    if (!patientId) return;
    callGetMdsList();
  }, [patientId, sortOptions, page, rowPerPage]);

  const callGetMdsFormId = (
    patientId: string,
    type: 'EVALUATION' | 'RECERTIFICATION' | 'DISCHARGE'
  ) => {
    dispatch(
      getMdsFormIdAction(patientId, {
        type,
      })
    );
  };

  const callMdsIdByTogle = (
    patientId: string,
    source: 'EVALUATION' | 'RECERTIFICATION' | 'DISCHARGE'
  ) => {
      dispatch(getMdsReformId(patientId));
      callGetMdsFormId(patientId, source);
  };

  const navigateToCreateMds = useCallback(
    (value: string) => {
      switch (value) {
        case 'eval':
          callMdsIdByTogle(patientId, 'EVALUATION');
          navigate(`${patients}/${patientDetails}${mdsCommunication}${evalMds}`, {
            state: {
              patientId,
              patientData,
            },
          });
          break;
        case 'recrti':
          callMdsIdByTogle(patientId, 'RECERTIFICATION');
          navigate(`${patients}/${patientDetails}${mdsCommunication}${recertiMds}`, {
            state: {
              patientId,
              patientData,
            },
          });
          break;
        case 'discharge':
          callMdsIdByTogle(patientId, 'DISCHARGE');
          navigate(`${patients}/${patientDetails}${mdsCommunication}${dischargeMds}`, {
            state: {
              patientId,
              patientData,
            },
          });
          break;
        default:
          break;
      }
    },
    [patientId]
  );

  const navigateToEditMds = useCallback(
    (mdsData: object) => {
      navigate(`${patients}/${patientDetails}${mdsCommunication}${editMdsCommunication}`, {
        state: {
          patientId,
          mdsData,
          patientData,
        },
      });
    },
    [patientId]
  );

  const navigateToViewMds = useCallback(
    (mdsData: object) => {
      navigate(`${patients}/${patientDetails}${mdsCommunication}${viewMdsCommunication}`, {
        state: {
          patientId,
          mdsData,
          patientData,
        },
      });
    },
    [patientId]
  );

  const handleStatusChange = (value: string[]) => {
    setInitialPage();
    callGetMdsList(value.length === 0 ? null : value[0]);
    setStatusFilter(value);
  };

  const onPageChange = (event: any, newPage: number) => {
    setPage(newPage);
  };

  const onRowsPerPageChange = (event: any) => {
    const newRowsPerPage = event.target.value;
    setRowPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSorting = (options: SortOption) => {
    setInitialPage();
    setSortOptions({
      sortBy: options.sortBy,
      sortDirection: options.sortDirection || 'desc',
    });
  };

  const mdsData: Option[] = [
    {
      label: '5-Day / Quarterly / Annual',
      value: 'eval',
    },
    {
      label: 'IPA assessment',
      value: 'recrti',
    },
    {
      label: 'Discharge Assessment',
      value: 'discharge',
    },
  ];

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    <Card sx={{ padding: '1.5rem' }}>
      <Stack
        gap="1rem"
        sx={{ padding: '0 1rem 1rem 1rem' }}
        flexDirection="row"
      >
        <MultiSelect
          label="Status"
          onChange={handleStatusChange}
          options={statusOptions}
          value={filterStatus}
          isMulti={false}
        />
      </Stack>

      <Stack>
        {!loadMdsFormList && (
          <>
            <Stack
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'space-between'}
            >
              <Stack
                gap="1rem"
                alignItems="center"
                flexDirection="row"
                sx={{ marginBottom: '1rem' }}
              >
                <h3 style={{ margin: '0px' }}>MDS Assessment</h3>
                <Stack
                  flexDirection="row"
                  sx={{
                    borderRadius: '12px',
                    display: 'flex',
                    border: '1px solid #00000042',
                    color: '#111927',
                    padding: '3px 10px',
                  }}
                >
                  <Typography variant="subtitle2">{`${getMdsFormList?.data?.data?.totalElements} MDS`}</Typography>
                </Stack>
              </Stack>
              <Stack>
                <Tooltip
                  title="Only DOR can create the MDS assessment"
                  followCursor
                >
                  <span>
                    <Button
                      color="inherit"
                      startIcon={
                        <SvgIcon>
                          <Plus />
                        </SvgIcon>
                      }
                      sx={{ color: 'primary.dark' }}
                      onClick={handleClick}
                      disabled={!isDor}
                    >
                      Create a New MDS Assessment
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleCloseMenu}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
              sx={{ display: 'initial' }}
            >
              {mdsData.map((item: Option) => {
                return (
                  <MenuItem
                    key={item.value}
                    onClick={() => navigateToCreateMds(item.value)}
                  >
                    <Grid
                      container
                      alignItems={'center'}
                      gap={1}
                    >
                      <Grid item>{item.label}</Grid>
                    </Grid>
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        )}

        {!loadMdsFormList ? (
          <>
            {getMdsFormList?.data?.data?.content?.length > 0 ? (
              <ListingTable
                items={getMdsFormList?.data?.data?.content}
                count={getMdsFormList?.data?.data?.totalElements}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                onViewPage={navigateToViewMds}
                onEditEvent={navigateToEditMds}
                rowsPerPage={rowPerPage}
                handleSortRequest={handleSorting}
                sortOptions={sortOptions}
                isFromPagin={true}
                tableHead={[
                  {
                    label: 'Assessment Date',
                    value: 'assessmentDate',
                    sort: true,
                  },
                  {
                    label: 'Assessment Type',
                    value: 'assessmentType',
                    sort: true,
                  },
                  {
                    label: 'Status',
                    value: 'status',
                    action: 'mds',
                  },
                  {
                    label: '',
                    value: 'edit',
                    action: 'mds',
                  },
                  {
                    label: '',
                    value: 'arrow',
                    action: 'mds',
                  },
                ]}
              />
            ) : (
              <Typography sx={{ margin: '1rem auto' }}>No Record Founds</Typography>
            )}
          </>
        ) : (
          <>
            {Array(12)
              .fill(null)
              .map((_, index) => (
                <Skeleton key={index} />
              ))}
          </>
        )}
      </Stack>
    </Card>
  );
}

export default MdsCommunication;
